/**
 * CausticsProjector — real projected caustics (Evan Wallace's WebGL-water
 * technique): render the water surface grid WARPED to where each vertex's
 * refracted sun ray hits the receiver plane; the fragment stage compares the
 * screen-space derivative area of the original vs warped positions — where
 * rays converge the ratio spikes, and that IS the caustic intensity.
 *
 * The result renders additively into a small ortho render target over the
 * water footprint (local space, mapped exactly like the plane UV the water
 * material already samples foam with). The water material modulates its
 * refracted floor with it in `causticMode: 'projected'`; the texture is also
 * exposed so scenes can light submerged objects with the same pattern.
 *
 * Surface data comes from the SAME source as the visuals: the shared TSL
 * Gerstner evaluators, or the FFT cascade textures in FFT mode.
 */

import * as THREE from 'three/webgpu';
import {
  Fn,
  abs,
  clamp,
  dFdx,
  dFdy,
  float,
  max,
  positionLocal,
  refract,
  texture,
  uniform,
  varying,
  vec2,
  vec3,
  vec4,
} from 'three/tsl';
import type { GerstnerWave } from './GerstnerWaves.js';
import { makeTslGerstner, type TslWaveModulation } from './tsl-gerstner.js';
import type { WaveProvider } from './WaveProvider.js';

export interface CausticsProjectorConfig {
  /** Water footprint (world units) — the projection + texture domain. */
  sizeX: number;
  sizeZ: number;
  /** Receiver plane depth below the water surface in metres. Default 2. */
  floorDepth?: number;
  /** Render-target resolution. Default 512. */
  resolution?: number;
  /** Surface mesh segments for the warp. Default 128. */
  segments?: number;
  /** Intensity scale. Default 1. */
  strength?: number;
  /** Wave source: analytic Gerstner set… */
  waves?: GerstnerWave[];
  /** …or FFT cascade textures (displacement + patch sizes). */
  fft?: { displacement: THREE.Texture[]; derivatives: THREE.Texture[]; patchSizes: number[] };
  /**
   * The water material's sea-state nodes (RealisticWaterHandles.waveModulationNodes)
   * so a wind-scaled/turned sea refracts the same surface it draws.
   */
  waveModulation?: TslWaveModulation | null;
  /**
   * Shader gaps R5: the sea's WaveProvider. A Gerstner provider supplies `waves`; a non-Gerstner one (the FFT patch)
   * supplies the refracting surface's height and normal. Its patch is world-anchored, so set `uWorldOffset` to the
   * water mesh's render-space position like the material's.
   */
  waveProvider?: WaveProvider | null;
}

export class CausticsProjector {
  /** The caustic light map (single channel in .r), plane-UV addressed. */
  readonly texture: THREE.Texture;
  readonly uTime = uniform(0);
  /** World offset of the water mesh (keeps FFT sampling world-anchored). */
  readonly uWorldOffset = uniform(new THREE.Vector2(0, 0));
  readonly uSunDirection = uniform(new THREE.Vector3(0.4, 0.8, 0.2).normalize());

  private readonly rt: THREE.RenderTarget;
  private readonly scene = new THREE.Scene();
  private readonly camera: THREE.OrthographicCamera;
  private readonly mesh: THREE.Mesh;

  constructor(config: CausticsProjectorConfig) {
    const res = Math.min(2048, Math.max(64, config.resolution ?? 512));
    const segments = Math.min(256, Math.max(32, config.segments ?? 128));
    const { sizeX, sizeZ } = config;
    const floorDepth = Math.max(0.05, config.floorDepth ?? 2);
    const strength = config.strength ?? 1;

    this.rt = new THREE.RenderTarget(res, res, {
      type: THREE.HalfFloatType,
      format: THREE.RGBAFormat,
      depthBuffer: false,
    });
    this.rt.texture.wrapS = THREE.ClampToEdgeWrapping;
    this.rt.texture.wrapT = THREE.ClampToEdgeWrapping;
    this.rt.texture.magFilter = THREE.LinearFilter;
    this.rt.texture.minFilter = THREE.LinearFilter;
    this.texture = this.rt.texture;

    // Ortho camera exists for renderer bookkeeping; the vertex stage writes
    // clip space directly so its transform is identity-like.
    this.camera = new THREE.OrthographicCamera(-1, 1, 1, -1, 0.1, 10);
    this.camera.position.set(0, 5, 0);
    this.camera.lookAt(0, 0, 0);

    const geometry = new THREE.PlaneGeometry(sizeX, sizeZ, segments, segments);
    geometry.rotateX(-Math.PI / 2);

    const material = new THREE.MeshBasicNodeMaterial();
    material.transparent = true;
    material.blending = THREE.AdditiveBlending;
    material.depthTest = false;
    material.depthWrite = false;
    material.side = THREE.DoubleSide;

    const provider = config.waveProvider ?? null;
    const pField = provider && provider.kind !== 'gerstner' ? provider.tsl(this.uTime, uniform(1 / 60), config.waveModulation ?? null, 0) : null;
    const pWaves = config.waves ?? (provider && provider.kind === 'gerstner' ? provider.waves : null);
    const gerstner = !pField && pWaves ? makeTslGerstner(pWaves, this.uTime, config.waveModulation ?? undefined) : null;
    const fft = config.fft ?? null;
    const uTime = this.uTime;
    const uWorldOffset = this.uWorldOffset;
    const uSun = this.uSunDirection;
    void uTime;

    // Original (undisplaced) local grid position and the warped receiver hit,
    // both passed to the fragment stage for the derivative area ratio.
    const vOld = varying(vec2(0, 0));
    const vNew = varying(vec2(0, 0));

    material.vertexNode = Fn(() => {
      const lx = positionLocal.x;
      const lz = positionLocal.z;
      const wx = lx.add(uWorldOffset.x);
      const wz = lz.add(uWorldOffset.y);

      // Surface height + normal from the same source as the visuals.
      let surfY: ReturnType<typeof float>;
      // Node type varies by branch (normalize/vec3/const) — keep it loose.
      let normal: ReturnType<typeof vec3> | ReturnType<typeof float>;
      if (pField) {
        surfY = pField.vertex.displacement(wx, wz).dy;
        normal = pField.surface.normal(wx, wz) as unknown as ReturnType<typeof vec3>;
      } else if (fft) {
        let dy: ReturnType<typeof float> = float(0);
        let sx: ReturnType<typeof float> = float(0);
        let sz: ReturnType<typeof float> = float(0);
        for (let i = 0; i < fft.displacement.length; i++) {
          const uvW = vec2(wx, wz).div(fft.patchSizes[i]);
          dy = dy.add(texture(fft.displacement[i], uvW).y);
          const dv = texture(fft.derivatives[i], uvW);
          sx = sx.add(dv.x);
          sz = sz.add(dv.y);
        }
        surfY = dy;
        normal = vec3(sx.negate(), 1, sz.negate()).normalize() as unknown as ReturnType<typeof vec3>;
      } else if (gerstner) {
        surfY = gerstner.displacement(wx, wz).dy;
        normal = gerstner.normal(wx, wz) as unknown as ReturnType<typeof vec3>;
      } else {
        surfY = float(0);
        normal = vec3(0, 1, 0);
      }

      // Refract the sun ray (downward incident) at the surface, then march
      // to the receiver plane at y = −floorDepth (water-local).
      const incident = uSun.normalize().negate();
      const refr = refract(incident, normal, 1 / 1.33);
      const denom = refr.y.min(-1e-3); // always downward
      const t = float(-floorDepth).sub(surfY).div(denom);
      const hitX = lx.add(refr.x.mul(t));
      const hitZ = lz.add(refr.z.mul(t));

      vOld.assign(vec2(lx, lz));
      vNew.assign(vec2(hitX, hitZ));

      // Direct clip-space output over the footprint (vertexNode MUST return
      // a clip vec4 — the known r182 gotcha).
      // WHY −z: three r185 samples a render target with v = 0 at its TOP row (NDC y = +1) on both backends (THE TOP-LEFT
      // LAW), and the sea reads this map at (u, 1 − v_plane) — v 0 at z = −size/2. +z at NDC +1 mirrored the map in z.
      return vec4(hitX.mul(2 / sizeX), hitZ.mul(-2 / sizeZ), 0, 1);
    })();

    material.colorNode = Fn(() => {
      // Ray-bundle compression: area(original cell) / area(warped cell).
      const oldDx = dFdx(vOld);
      const oldDy = dFdy(vOld);
      const newDx = dFdx(vNew);
      const newDy = dFdy(vNew);
      const oldArea = abs(oldDx.x.mul(oldDy.y).sub(oldDx.y.mul(oldDy.x))).max(1e-8);
      const newArea = abs(newDx.x.mul(newDy.y).sub(newDx.y.mul(newDy.x))).max(1e-8);
      const intensity = clamp(oldArea.div(newArea).sub(0.2).mul(0.25 * strength), 0, 4);
      return vec4(intensity, intensity, intensity, 1);
    })();

    this.mesh = new THREE.Mesh(geometry, material);
    this.mesh.frustumCulled = false;
    this.scene.add(this.mesh);
    void max; // (imported for parity with sibling files; used via .max())
  }

  /** Render the caustic map for the current clock/sun (small ortho pass). */
  render(renderer: { getRenderTarget(): unknown; setRenderTarget(rt: unknown): void; render(s: unknown, c: unknown): void }, time: number): void {
    this.uTime.value = time;
    const prev = renderer.getRenderTarget();
    renderer.setRenderTarget(this.rt);
    renderer.render(this.scene, this.camera);
    renderer.setRenderTarget(prev);
  }

  dispose(): void {
    this.rt.dispose();
    (this.mesh.material as THREE.Material).dispose();
    this.mesh.geometry.dispose();
  }
}
